import { useEffect, useState } from 'react';
import { Outlet, useLocation, matchPath } from 'react-router-dom';

import Header from '../components/header/Header';
import Footer from '../components/footer/Footer';
import useCookieConsent from '../hooks/useCookieConsent';
import CookieConsentModal from '../components/docs/CookieConsentModal';
import '../index.css';

import MobileMenu from '../components/header/navMenu/MobileMenu';
import { useHeader } from '../context/HeaderContext';
import NetworkWatcher from "../components/network/NetworkWatcher";
/* import LaunchGate from "@/components/security/LaunchGate"; */

import { useBackendStatus } from '@/api/useBackendStatus';
import OfflineBackendPage from '@/pages/OffineBackendPage';
import PaginaCookieRechazada from '@/components/docs/PaginaCookieRechazada';

function useIsSmallScreen() {
  const [isSmall, setIsSmall] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  });

  useEffect(() => {
    const handleResize = () => setIsSmall(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isSmall;
}

export default function Root() {

  const { hideHeader } = useHeader();
  const location = useLocation();
  const isSmallScreen = useIsSmallScreen();


  const {
    cookiesAccepted,
    acceptCookies,
    rejectCookies,
    loadingCookies
  } = useCookieConsent();

  const { backendDown } = useBackendStatus();
  const [offline, setOffline] = useState(false);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setOffline(backendDown);
  }, [backendDown]);

  if (offline) {
    return <OfflineBackendPage />;
  }


  if (loadingCookies) return null;

  /*  const isBackoffice = location.pathname.startsWith("/backoffice"); */

  // ----------------------------
  // ROUTES CONFIG
  // ----------------------------
  const hideFooterPaths = [
    '/suscribirse',
    '/editaDatosProfesional/:profesion/:username',
    '/editProfesional/:profesionalId/:profesionId/:perfilId',
    '/editaUsuario',
    '/contacto',
    '/sobreNosotros',
    '/sign',
    '/avisoLegal',
    '/politicaCookies',
    '/politicaPrivacidad',
    '/errorPage',
    '/success',
    '/profesional/:profesionalId/profesiones',
    '/contratoServicios',
    '/renovar/:perfil_profesional_id',
    '/backoffice',
    '/InsertarSecciones',
    '/editarSecciones',
    '/borrarSecciones',
    '/insertarProfesiones',
    '/editarProfesion',
    '/borrarProfesiones',
    '/buscarProfesionales',
    '/editarProfesional',
    '/bajaProfesional',
    '/editarUsuarioAdmin',
    '/buscarUsuarioAdmin',
    '/gestionUsuarioAdmin',
    '/codigosAfiliado',
    '/afiliados',
    '/crearAfiliado',
    '/resumenAfiliado',
    '/editarAfiliado',
    '/bajaAfiliado',
    '/superAdmin',
    '/crearAdmin',
    '/facturasResumen',
    '/contratosResumen',
    '/editar-Profesional',
    '/panelAfiliado',
    '/paginaCookieRechazada',
    '/cookies-bloqueadas',
    '/introduceNuevaContrasena',
    '/codigoRecuperacion',
    '/VerificarCodigo',
    '/popuBajaDefinitiva',
    '/LandingPage',
    '/headerLanding',
    '/ofertasLanding',
    '/empleoLanding',
    '/contactoLanding',
    '/quienessomoLanding',
    '/landingPrincipal/:id',
    '/landing/:id',
    'editar-landing/:id',
    'editar-landing/:id/ofertas',
    'editar-landing/:id/empleo',
    'editar-landing/:id/contacto',
    'editar-landing/:id/quienes-somos',
  ];

  const hideHeaderPaths = [
    '/avisoLegal',
    '/politicaCookies',
    '/politicaPrivacidad',
    '/errorPage',
    '/contratoServicios',
    '/InsertarSecciones',
    '/editarSecciones',
    '/borrarSecciones',
    '/insertarProfesiones',
    '/editarProfesion',
    '/borrarProfesiones',
    '/buscarProfesionales',
    '/editarProfesional',
    '/bajaProfesional',
    '/editarUsuarioAdmin',
    '/buscarUsuarioAdmin',
    '/gestionUsuarioAdmin',
    '/codigosAfiliado',
    '/afiliados',
    '/crearAfiliado',
    '/resumenAfiliado',
    '/editarAfiliado',
    '/bajaAfiliado',
    '/superAdmin',
    '/crearAdmin',
    '/facturasResumen',
    '/contratosResumen',
    '/editar-Profesional',
    '/success',
    '/panelAfiliado',
    '/paginaCookieRechazada',
    '/cookies-bloqueadas',
    '/introduceNuevaContrasena',
    '/codigoRecuperacion',
    '/VerificarCodigo',
    'LandingPage',
    'headerLanding',
    '/ofertasLanding',
    '/empleoLanding',
    '/contactoLanding',
    '/quienessomoLanding',
    '/landingPrincipal/:id',
    '/landing/:id',
    'editar-landing/:id',
    'editar-landing/:id/ofertas',
    'editar-landing/:id/empleo',
    'editar-landing/:id/contacto',
    'editar-landing/:id/quienes-somos',
  ];

  const shouldHideFooter =
    hideFooterPaths.some(path =>
      !!matchPath({ path, end: false }, location.pathname)
    ) ||
    (location.pathname === '/errorPage' && isSmallScreen);

  const shouldHideHeader =
    hideHeaderPaths.some(path =>
      !!matchPath({ path, end: false }, location.pathname)
    );

  const isPolicyPage = ['/avisoLegal', '/politicaCookies'].some(path =>
    !!matchPath({ path, end: false }, location.pathname)
  );

  // ✅ ÚNICA FUENTE DE VERDAD
  const showConsentModal =
    cookiesAccepted === null && !loadingCookies;

  return (
   /*  <LaunchGate> */
      <div className="relative flex flex-col min-h-screen">

        <NetworkWatcher />

        {!hideHeader && !shouldHideHeader && <Header />}

        <MobileMenu
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />

        {/* 🍪 COOKIE MODAL */}
        {!isPolicyPage && showConsentModal && (
          <CookieConsentModal
            onAccept={(optionalCookies) => {
              acceptCookies(optionalCookies);

              // fuerza re-evaluación del router inmediatamente
              window.dispatchEvent(new Event("cookie-updated"));
            }}
            onReject={() => {
              rejectCookies();
            }}


            onClose={() => { }}
          />
        )}

        <main className="relative z-0 flex-1 flex items-center justify-center w-full">

          {cookiesAccepted === false && !showConsentModal ? (
            <PaginaCookieRechazada
              onReconsider={() => {
                acceptCookies({
                  analytics: false,
                  personalization: false,
                  ads: false,
                });
              }}
            />
          ) : (
            <Outlet />
          )}

        </main>

        {!shouldHideFooter && <Footer />}

      </div>
 /*    </LaunchGate> */
  );
}